import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, Wrench, CheckCircle, Warning, PencilSimple, Trash, ClockCounterClockwise } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useT } from '@/i18n/LanguageContext';
import { useAuth, canEditMfg, hasCap } from '@/auth/AuthContext';
import { mfgApi } from './mfgApi';
import { DataTable } from '@/components/ui/DataTable';
import ScheduleFormDialog from './ScheduleFormDialog';
import CompleteScheduleDialog from './CompleteScheduleDialog';
import CorrectiveDialog from './CorrectiveDialog';

export default function Maintenance() {
  const { t } = useT();
  const { user } = useAuth();
  const canWrite = canEditMfg(user);
  const canDeleteMfg = hasCap(user, 'mfg_delete');
  const [stats, setStats] = useState(null);
  const [due, setDue] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [logType, setLogType] = useState('all');
  const [schedDialog, setSchedDialog] = useState({ open: false, schedule: null });
  const [completeFor, setCompleteFor] = useState(null);
  const [corrOpen, setCorrOpen] = useState(false);

  const load = useCallback(() => {
    mfgApi.equipmentStats().then(setStats).catch(() => {});
    mfgApi.schedules({ due: true }).then(setDue).catch(() => {});
    mfgApi.schedules().then(setSchedules).catch(() => {});
    mfgApi.logs(logType === 'all' ? {} : { type: logType }).then(setLogs).catch(() => {});
  }, [logType]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { mfgApi.equipment().then(setEquipment).catch(() => {}); }, []);

  const delSchedule = async (s) => {
    if (!window.confirm(`${s.title} を削除しますか？`)) return;
    try { await mfgApi.deleteSchedule(s.id); toast.success(t('mfg.common.deleted')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
  };
  const closeLog = async (l) => {
    try { await mfgApi.updateLog(l.id, { status: 'closed', restore_operational: true }); toast.success(t('mfg.common.saved')); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
  };

  const dueBadge = (s) => s.overdue
    ? <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">{t('mfg.alert.overdue')}</Badge>
    : <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{t('mfg.alert.dueSoon')}</Badge>;

  return (
    <div className="space-y-5 page-enter" data-testid="mfg-maintenance">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{t('mfg.maint.title')}</h1>
        {canWrite && (
          <div className="flex gap-2">
            <Button variant="outline" data-testid="maint-new-schedule-btn" onClick={() => setSchedDialog({ open: true, schedule: null })} className="h-11 border-orange-200 text-orange-600 hover:bg-orange-50"><Plus size={17} className="mr-1.5" /> {t('mfg.maint.newSchedule')}</Button>
            <Button data-testid="maint-new-corrective-btn" onClick={() => setCorrOpen(true)} className="h-11 bg-teal-700 hover:bg-teal-800"><Wrench size={17} className="mr-1.5" /> {t('mfg.maint.newCorrective')}</Button>
          </div>
        )}
      </div>

      {/* Alert banner */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4" data-testid="maint-stat-overdue"><div className="text-2xl font-bold text-rose-700 tabular-nums">{stats?.overdue ?? 0}</div><div className="text-xs text-rose-600 mt-0.5 flex items-center gap-1"><Warning size={13} /> {t('mfg.eq.stat.overdue')}</div></div>
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4" data-testid="maint-stat-duesoon"><div className="text-2xl font-bold text-amber-700 tabular-nums">{stats?.due_soon ?? 0}</div><div className="text-xs text-amber-600 mt-0.5 flex items-center gap-1"><ClockCounterClockwise size={13} /> {t('mfg.alert.dueSoon')}</div></div>
        <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-4" data-testid="maint-stat-openrepairs"><div className="text-2xl font-bold text-violet-700 tabular-nums">{stats?.open_corrective ?? 0}</div><div className="text-xs text-violet-600 mt-0.5 flex items-center gap-1"><Wrench size={13} /> {t('mfg.eq.stat.openRepairs')}</div></div>
      </div>

      {/* Due now */}
      <section className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden" data-testid="maint-due">
        <div className="px-4 py-3 border-b border-zinc-200"><h2 className="text-sm font-semibold text-zinc-800">{t('mfg.maint.dueNow')}</h2></div>
        {due.length === 0 ? <p className="px-4 py-8 text-center text-sm text-zinc-400">{t('mfg.maint.noDue')}</p> : (
          <ul className="divide-y divide-zinc-200">
            {due.map((s) => (
              <li key={s.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`maint-due-${s.id}`}>
                {dueBadge(s)}
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-zinc-800 truncate">{s.equipment_no} {s.equipment_name} — {s.title}</p>
                  <p className="text-xs text-zinc-400">{t('mfg.maint.nextDue')}: {s.next_due_on || '—'}</p>
                </div>
                {canWrite && <Button size="sm" data-testid={`maint-due-complete-${s.id}`} onClick={() => setCompleteFor(s)} className="h-8 bg-teal-700 hover:bg-teal-800"><CheckCircle size={14} className="mr-1" /> {t('mfg.maint.complete')}</Button>}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* All schedules */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-zinc-800 px-1">{t('mfg.maint.schedules')}</h2>
        <DataTable
          dataTestId="maint-schedule-table"
          rows={schedules}
          rowKey={(s) => s.id}
          empty={{ icon: <Wrench size={36} weight="duotone" className="text-zinc-300 mx-auto mb-2" />, text: t('mfg.maint.noSchedules'), testid: 'maint-schedule-empty' }}
          columns={[
            { key: 'equipment', primary: true, header: t('mfg.maint.equipment'), cell: (s) => <span><span className="font-mono text-xs text-zinc-500">{s.equipment_no}</span> {s.equipment_name}</span> },
            { key: 'title', header: t('mfg.maint.scheduleTitle'), cell: (s) => <span className="text-zinc-700">{s.title}{!s.active && <span className="text-[10px] text-zinc-400 ml-1">(無効)</span>}</span> },
            { key: 'next_due', header: t('mfg.maint.nextDue'), cell: (s) => <span className={s.overdue ? 'text-rose-600 font-semibold' : (s.due_soon ? 'text-amber-600 font-semibold' : 'text-zinc-600')}>{s.next_due_on || '—'}</span> },
            { key: 'actions', actions: true, header: '', cell: (s) => (<>
              {canWrite && <Button size="sm" variant="outline" data-testid={`maint-sched-complete-${s.id}`} onClick={() => setCompleteFor(s)} className="h-8 text-xs"><CheckCircle size={13} className="mr-1" /> {t('mfg.maint.complete')}</Button>}
              {canWrite && <Button size="sm" variant="ghost" onClick={() => setSchedDialog({ open: true, schedule: s })} className="h-8 px-2 text-zinc-500"><PencilSimple size={14} /></Button>}
              {canDeleteMfg && <Button size="sm" variant="ghost" onClick={() => delSchedule(s)} className="h-8 px-2 text-rose-600"><Trash size={14} /></Button>}
            </>) },
          ]}
        />
      </section>

      {/* Logs */}
      <section className="bg-white rounded-xl border border-zinc-200 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-200">
          <h2 className="text-sm font-semibold text-zinc-800">{t('mfg.maint.logs')}</h2>
          <div className="flex gap-1.5">
            {['all', 'preventive', 'corrective'].map((tp) => (
              <button key={tp} onClick={() => setLogType(tp)} data-testid={`maint-log-filter-${tp}`}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border ${logType === tp ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-zinc-600 border-zinc-200'}`}>
                {tp === 'all' ? t('mfg.common.all') : t(`mfg.maint.type.${tp}`)}
              </button>
            ))}
          </div>
        </div>
        {logs.length === 0 ? <p className="px-4 py-8 text-center text-sm text-zinc-400">{t('mfg.maint.noLogs')}</p> : (
          <ul className="divide-y divide-zinc-200" data-testid="maint-log-list">
            {logs.map((l) => (
              <li key={l.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                <Badge variant="outline" className={l.type === 'preventive' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-rose-50 text-rose-700 border-rose-200'}>{l.type_label}</Badge>
                <div className="min-w-0 flex-1">
                  <p className="text-zinc-800 truncate"><span className="font-mono text-xs text-zinc-500">{l.equipment_no}</span> {l.action || l.symptom || l.schedule_title || '—'}</p>
                  <p className="text-[11px] text-zinc-400">{l.performed_on}{l.performed_by ? ` · ${l.performed_by}` : ''}{l.downtime_hours != null ? ` · ${l.downtime_hours}h` : ''}</p>
                </div>
                {l.type === 'corrective' && (l.status === 'open'
                  ? (canWrite ? <Button size="sm" variant="outline" data-testid={`maint-log-close-${l.id}`} onClick={() => closeLog(l)} className="h-7 text-xs">{t('mfg.maint.close')}</Button> : <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">{t('mfg.maint.statusOpen')}</Badge>)
                  : <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">{t('mfg.maint.statusClosed')}</Badge>)}
              </li>
            ))}
          </ul>
        )}
      </section>

      <ScheduleFormDialog open={schedDialog.open} schedule={schedDialog.schedule} equipmentList={equipment}
        onClose={() => setSchedDialog({ open: false, schedule: null })}
        onSaved={() => { setSchedDialog({ open: false, schedule: null }); load(); }} />
      <CompleteScheduleDialog open={!!completeFor} schedule={completeFor}
        onClose={() => setCompleteFor(null)} onDone={() => { setCompleteFor(null); load(); }} />
      <CorrectiveDialog open={corrOpen} equipmentList={equipment}
        onClose={() => setCorrOpen(false)} onDone={() => { setCorrOpen(false); load(); }} />
    </div>
  );
}
